<template>
  <div class="w-full bg-slate-50 min-h-screen p-4" style="font-family: 'Inter', sans-serif;">
    <div class="max-w-7xl mx-auto space-y-8">
      
      <!-- HEADER -->
      <div class="mb-6">
        <h1 class="text-2xl font-bold text-slate-700">Situação de Frota</h1>
        <p class="text-sm text-slate-500">Controle de status e disponibilidade dos veículos</p>
      </div>

      <!-- ÁREA DE FILTRO -->
      <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm mb-8">
          <div class="px-6 py-4" style="background-color: #333333;">
              <h2 class="text-white font-bold flex items-center gap-2" style="font-size: 0.85rem; letter-spacing: 0.05em;">
                  <v-icon color="orange" small class="mr-2">mdi-magnify</v-icon>
                  FILTRAR CONSULTA
              </h2>
          </div>
          <div class="p-8">
              <div class="grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
                  <div class="md:col-span-4">
                      <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Mês e Ano</label>
                      <input 
                        v-model="filter.date"
                        type="month"
                        class="w-full bg-slate-50 border border-slate-200 rounded px-3 py-2 text-sm text-slate-700 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-colors h-[40px]"
                      >
                  </div>
                  <div class="md:col-span-6">
                      <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Prefeitura</label>
                      <select
                        v-model="filter.prefecture_id"
                        class="w-full bg-slate-50 border border-slate-200 rounded px-3 py-2 text-sm text-slate-700 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-colors h-[40px]"
                      >
                        <option :value="null" disabled>Selecione a Prefeitura...</option>
                        <option v-for="item in prefectures" :key="item.prefecture_id" :value="item.prefecture_id">
                          {{ item.prefecture_social_name }}
                        </option>
                      </select>
                  </div>
                  <div class="md:col-span-2">
                      <button 
                        @click="loadSituacao" 
                        :disabled="isButtonDisabled"
                        class="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:bg-slate-300 text-white font-bold text-[10px] px-6 py-3 rounded shadow transition-all uppercase tracking-widest h-[40px]"
                      >
                        FILTRAR
                      </button>
                  </div>
              </div>
          </div>
      </section>

      <!-- GRID DE SITUAÇÕES -->
      <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
          <!-- Toolbar do Grid -->
          <div class="px-4 py-4 flex items-center w-full" style="background-color: #333333;">
              <div class="flex items-center gap-2 flex-none">
                <v-icon color="orange" small>mdi-list-status</v-icon>
                <h2 class="text-white font-bold uppercase tracking-wider" style="font-size: 0.85rem; letter-spacing: 0.05em;">HISTÓRICO DE SITUAÇÕES</h2>
              </div>
              
              <div class="flex items-center gap-2 flex-1 justify-end">
                  <div class="flex-none">
                    <button 
                      @click="$router.push('/vehicles-manager/situacao/create')" 
                      class="bg-orange-500 hover:bg-orange-600 text-white shadow-lg flex items-center justify-center gap-2 rounded px-6 py-2 transition-all w-fit max-w-max whitespace-nowrap"
                    >
                      <v-icon color="white" small>mdi-plus</v-icon>
                      <span class="font-bold text-[10px] uppercase tracking-widest">NOVA SITUAÇÃO</span>
                    </button>
                  </div>

                  <button @click="showSearch = !showSearch" class="p-2 rounded hover:bg-slate-800 text-slate-400 transition-colors">
                     <v-icon color="currentColor" small>mdi-magnify</v-icon>
                  </button>
              </div>
          </div>

          <!-- Inline Search -->
           <v-expand-transition>
            <div v-if="showSearch" class="bg-slate-50 px-6 py-4 border-b border-slate-200 shadow-inner">
              <div class="flex items-center w-full px-3 py-2 bg-white border border-slate-300 rounded-lg shadow-sm focus-within:ring-1 focus-within:ring-orange-500 focus-within:border-orange-500 transition-all">
                  <v-icon color="#94a3b8" small class="mr-2">mdi-magnify</v-icon>
                  <input 
                    v-model="search" 
                    type="text" 
                    placeholder="Pesquisar por veículo ou data..." 
                    class="w-full text-sm text-slate-700 placeholder-slate-400 focus:outline-none bg-transparent border-none p-0"
                  >
              </div>
            </div>
           </v-expand-transition>

          <!-- Tabela -->
          <div class="overflow-x-auto">
             <v-data-table
              :headers="headers"
              :items="data"
              :search="search"
              :loading="loader"
              hide-default-footer
              class="custom-table"
            >
              <!-- Veículo -->
              <template v-slot:item.veiculo="{ item }">
                <div class="flex items-center gap-3 py-2">
                  <div class="w-8 h-8 rounded bg-slate-100 flex items-center justify-center">
                    <v-icon small color="#64748b">mdi-car</v-icon>
                  </div>
                  <span class="text-sm font-bold text-slate-700 uppercase">{{ item.veiculo }}</span>
                </div>
              </template>

              <!-- Data -->
              <template v-slot:item.data="{ item }">
                <span class="text-xs font-medium text-slate-600">{{ item.data }}</span>
              </template>

              <!-- Situação -->
              <template v-slot:item.situacao="{ item }">
                 <span 
                   :class="item.situacao == 1 ? 'bg-emerald-100 text-emerald-700' : item.situacao == 2 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'" 
                   class="px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider"
                 >
                    {{ item.situacao == 1 ? "Em Uso" : item.situacao == 2 ? "Em Manutenção" : "Baixada" }}
                  </span>
              </template>

              <!-- Ações -->
              <template v-slot:item.action="{ item }">
                 <button 
                   @click="deleteItem(item.id)"
                   class="p-2 rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-500 transition-colors"
                 >
                   <v-icon small color="currentColor">mdi-delete</v-icon>
                 </button>
              </template>
             </v-data-table>
          </div>
      </section>
    </div>

    <!-- Snackbar -->
    <v-snackbar
      multi-line
      :color="snack.color"
      v-model="snack.show"
      transition="fade-transition"
    >
      {{ snack.message }}
      <template v-slot:action="{ attrs }">
        <v-btn dark text v-bind="attrs" @click="snack.show = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
export default {
  props: ["id"],
  data() {
    return {
      search: "",
      loader: true,
      showSearch: false,
      data: [],
      prefectures: [],
      filter: {
        prefecture_id: null,
        date: null,
      },
      snack: {
        show: false,
        message: "",
        color: "",
      },
      headers: [
        { text: "Veículo/Máquina", value: "veiculo", sortable: false },
        { text: "Data", value: "data", sortable: false },
        { text: "Situação", value: "situacao", sortable: false },
        { text: "Ações", align: "right", value: "action", sortable: false },
      ],
    };
  },
  created() {
    this.initialize();
  },
  computed: {
    isButtonDisabled() {
      return !(this.filter.prefecture_id && this.filter.date);
    },
  },
  methods: {
    initialize() {
      this.loader = true;
      this.$store.dispatch("setLoader", this.loader);
      this.$http.get("/prefectures").then((r) => {
        this.prefectures = r.data;
        this.loader = false;
        this.$store.dispatch("setLoader", this.loader);
      });
    },
    loadSituacao() {
      this.loader = true;
      this.$store.dispatch("setLoader", this.loader);
      this.$http
        .get(
          `/vehicles/situacao/all/${
            this.filter.prefecture_id
          }/${this.filter.date.substring(0, 4)}/${this.filter.date.substring(
            5
          )}`
        )
        .then((r) => {
          this.data = r.data;
          this.loader = false;
          this.$store.dispatch("setLoader", this.loader);
        })
        .catch((e) => {
          this.loader = false;
          this.snack.show = true;
          this.snack.message = e;
          this.snack.type = "error";
          this.$store.dispatch("setLoader", this.loader);
        });
    },
    deleteItem(id) {
      let c = confirm("Deseja excluir o registro?");
      if (c) {
        this.$store.dispatch("setLoader", true);
        this.$http
          .delete(`/vehicles/situacao/${id}`)
          .then(() => {
            this.snack.show = true;
            this.snack.message = "Excluido com sucesso!";
            this.snack.color = "success";
            this.loadSituacao();
          })
          .catch((e) => {
            this.snack.show = true;
            this.snack.message = e.message;
            this.snack.color = "error";
            this.$store.dispatch("setLoader", false);
          });
      }
    },
  },
};
</script>

<style scoped>
.title {
  text-transform: uppercase;
  font-weight: 400;
}
.maxPage {
  width: 100%;
}
.search-field {
  margin-top: 10px;
}

/* Custom Table Override */
.custom-table ::v-deep table { background-color: transparent !important; border-collapse: separate; border-spacing: 0; }
.custom-table ::v-deep thead { background-color: #f8fafc !important; }
.custom-table ::v-deep th { 
  color: #64748b !important; 
  font-weight: 700 !important; 
  text-transform: uppercase !important; 
  font-size: 0.75rem !important; 
  background-color: #f8fafc !important;
  border-bottom: 1px solid #e2e8f0 !important;
  height: 48px !important;
}
.custom-table ::v-deep td { 
  border-bottom: 1px solid #f1f5f9 !important;
  color: #334155 !important;
  font-size: 0.875rem !important;
}
</style>
